import React, { HTMLAttributes, useContext, useMemo, useRef } from 'react';
import style from './Plan.module.scss';
import classnames from 'classnames';
import piggy_bank from '../../assets/images/piggy_bank.gif'
import arrow_bottom from '../../assets/images/arrow_bottom.svg'
import arrows from '../../assets/images/arrows.gif'
import { getPriceBlock } from '../Price';
import { getCostBlock } from '../Cost';
import { CurrentBadge } from '../CurrentBadge';
import { format, isValid } from 'date-fns';
import { AppContext, AppShortContext } from '../../context/AppContext';
import { seams } from '../../constants';
import PlanFeatures from './PlanFeatures';
import AdditionalPlan from './AdditionalPlan';
import { getName } from '../../utils/getPlanName';
import { IPlan } from '../../model';
import classNames from 'classnames';

export interface IProps {
  plan: IPlan;
  className?: HTMLAttributes<HTMLDivElement>['className'];
  spacerClassName?: HTMLAttributes<HTMLDivElement>['className'];
  wrapperClassName?: HTMLAttributes<HTMLDivElement>['className'];
}

const Plan = ({plan, ...props}: IProps) => {
  const contextData = useContext(AppContext);
  const fallbackContextData = useContext(AppShortContext);

  const {
    yearly,
    baseCheckoutPath,
    currentSubscriptions,
    showRenewPopup,
    toggleAdditionalPlan
  } = contextData ?? fallbackContextData as any ?? {};
  const ref = useRef<HTMLDivElement>(null)

  const mainPlan = plan.isAdditional ? plan.additional : plan;
  const isSeams = useMemo(() => seams?.includes(String(mainPlan?.plan_id)), [mainPlan]);
  const currentPrice = mainPlan?.current?.price;

  const yearAmount = mainPlan?.current?.interval === 'year'
    ? currentPrice
      ? Number(currentPrice)
      : mainPlan?.prices?.yearly?.unit_amount ?? 0
    : mainPlan?.prices?.yearly?.unit_amount ?? 0;

  const monthAmount = mainPlan?.current?.interval === 'month'
    ? currentPrice
      ? Number(currentPrice)
      : mainPlan?.prices?.monthly?.unit_amount ?? 0
    : mainPlan?.prices?.monthly?.unit_amount ?? 0;

  const costBlock = getCostBlock({
    yearly,
    yearAmount: yearAmount,
    monthAmount: monthAmount
  })

  const current = mainPlan?.current;

  const wpBadge = useMemo(() => {
    // @ts-ignore
    return window.styles?.[mainPlan.plan_id];
  }, []);


  const priceObjectCheck = mainPlan?.prices[yearly ? 'yearly' : 'monthly'];
  let showAdditional: boolean = true;
  if (typeof priceObjectCheck !== 'object') {
    if (plan.isAdditional) {
      showAdditional = false;
    } else {
      if (!plan.additional) return <></>;
      if (!plan.additional?.prices?.[yearly ? 'yearly' : 'monthly']) return <></>;
      return <Plan plan={plan.additional}/>
    }
  } else {
    if (!plan.isAdditional) {
      if (!plan.additional?.prices?.[yearly ? 'yearly' : 'monthly']) showAdditional = false;
    }
  }

  const priceObject = !yearly ? mainPlan?.prices?.monthly : mainPlan?.prices?.yearly;
  const canceledAtDate = new Date(Number(`${String(current?.subscription?.cancel_at)}000`))
  const subscriptionEndDate = new Date(Number(`${String(current?.subscription?.current_period_end)}000`))

  const currentBadge = CurrentBadge({plan, yearly: yearly ?? false, isAdditional: plan.isAdditional});
  const isUpgradePossible = mainPlan?.current?.interval === 'month' && currentPrice && (Number(currentPrice) > (mainPlan?.prices?.yearly?.unit_amount ?? 0) / 12);

  const showWpBadge = Boolean(!currentBadge && wpBadge?.text);

  const additionalButtons = (mainPlan && window.additionalButtons?.[mainPlan.plan_id]) || {};
  const additionalText = mainPlan && window?.additionalTexts?.[mainPlan.plan_id];

  const hasSubscriptions = Object.keys(currentSubscriptions || {}).length > 0;
  return (
    <div className={classNames(style.plan_wrapper, props.wrapperClassName)}>
      <div className={classNames(style.plan_space, props.spacerClassName)}/>
      <div
        className={classnames(
          style.plan,
          {[style.plan_current]: currentBadge},
          {[style.downgrade]: plan.disabled},
          {[style.seams]: isSeams},
          !currentBadge && wpBadge?.style ? {[style[wpBadge?.style]]: wpBadge?.style && wpBadge?.style !== 'default'} : {},
          {[style.wp_badge]: showWpBadge},
          props.className
        )}
        data-plan-id={plan.plan_id}>
        {showWpBadge ? <div className={style.wp_badge_item}>{wpBadge?.text}</div> : ''}
        <div className={style.plan_top_part} ref={ref}>
          {currentBadge}
          <h4 className={style.title}>
            {getName(plan, isSeams)}
          </h4>
          {current
            ? (<>
              {(isUpgradePossible
                ? (yearly
                  ? <>
                    {costBlock}
                  </>
                  : <div className={style.upgrade_annual}>
                    {currentPrice && (Number(currentPrice) * 12 > (mainPlan?.prices?.yearly?.unit_amount || 0))
                      ? <div className={style.save_money}>save <div
                        className={style.price}>
                        {getPriceBlock(Number(currentPrice) * 12 - (mainPlan?.prices?.yearly?.unit_amount || 0))}
                      </div>
                        <sub>/year</sub>
                      </div>
                      : ''
                    }
                    <div
                      className={style.payment_period}>billed annually
                      ${(mainPlan?.prices?.yearly?.unit_amount || 0) / 100}
                    </div>
                  </div>)
                : (yearly ? '' : <div className={style.cost}>
                  <div className={style.price}>{getPriceBlock(Number(currentPrice))}</div>
                </div>))}
              <div className={style.cancel_row}>
                {(current.subscription?.cancel_at ? <div className={style.canceled}>Canceled</div> : '')}
                {(current.subscription?.cancel_at ? (canceledAtDate ? <>
                    <div className={style.next_payment}>
                      Active until {canceledAtDate.toDateString()}
                    </div>
                  </> : '')
                  : (subscriptionEndDate && isValid(subscriptionEndDate) ? <div className={style.next_payment}>
                    <span>Next payment {getPriceBlock(Number(current.price))} on {format(subscriptionEndDate, 'PP')}</span>
                    <div
                      className={classnames(style.renew_button)}
                      onClick={() => {
                        showRenewPopup?.({plan: mainPlan})
                      }}
                    >
                      Renew Now
                    </div>
                  </div> : ''))}
              </div>
            </>)
            : <>
              {costBlock}
            </>}
          {showAdditional ? <AdditionalPlan {...{
            plan,
            yearAmount,
            monthAmount,
            setState: (state: boolean) => toggleAdditionalPlan?.(plan.plan_id, state),
            state: !!plan.isAdditional
          }} /> : ''}
        </div>


        {mainPlan && (mainPlan.features?.length || window?.shortDescriptions?.[mainPlan.plan_id]) ? (<>
          <hr className={style.divider}/>
          <PlanFeatures plan={mainPlan}/>
        </>) : <></>}
        {additionalText
          ? <div className={style.disclaimer}
                 dangerouslySetInnerHTML={{__html: additionalText}}/>
          : <></>}
        {!plan.disabled ? current
          ? (<>
            {(isUpgradePossible
              ? (yearly
                ? <>
                  <div className={style.upgrade}>
                    <a href={`${baseCheckoutPath}?price_id=${mainPlan?.prices?.yearly?.price_id}`}
                       className={classnames('button-primary button-primary_md upgrade-button', style.upgrade_button)}>
                      <img src={piggy_bank} alt="" style={{width: 24, height: 24, margin: '-5px 5px 0 -15px'}}/>
                      Upgrade to Annual Plan
                    </a>
                  </div>
                </>
                : <div className={style.upgrade_annual}>
                  <a href={`${baseCheckoutPath}?price_id=${mainPlan?.prices?.yearly?.price_id}`}
                     className={classnames('button-primary button-primary_md upgrade-button', style.upgrade_button)}>
                    Upgrade to annual plan
                  </a>
                  <div className={style.payment_period}>billed annually
                    ${(mainPlan?.prices?.yearly?.unit_amount || 0) / 100}
                  </div>
                </div>)
              : '')}
            {(current.subscription?.cancel_at ? (canceledAtDate ? <a
              href={`${baseCheckoutPath}?price_id=${priceObject?.price_id}`}
              className={classnames('button-primary button-primary_md')}
              style={{width: '100%', marginTop: 20}}
            >
              Don't Cancel Subscription
            </a> : '') : '')}
          </>)
          : <>
            <div className={classnames(style.upgrade)}>
              <a
                href={`${baseCheckoutPath}?price_id=${priceObject?.price_id}`}
                className={classnames('button-primary button-primary_md upgrade-button', style.upgrade_button)}>
                {hasSubscriptions ? (<>
                    <img src={arrows} alt="" style={{width: 40, height: 40, margin: '0 -5px 0 -15px'}}/>
                    Upgrade
                  </>)
                  : (<>
                    {!isSeams ? 'Subscribe Now!' : 'Get All the Benefits'}
                  </>)}
              </a>
            </div>
          </> : <></>}
        {additionalButtons.download_button_link ?
          <a
            href={additionalButtons.download_button_link}
            className={classnames(style.additional_button, style.download)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={arrow_bottom} />
            {additionalButtons.download_button_label || 'Download CSV sample'}
          </a>
          : ''}
        {additionalButtons.learn_more_button_link ?
          <a
            href={additionalButtons.learn_more_button_link}
            className={classnames(style.additional_button, style.learn_more)}
            target="_blank"
            rel="noopener noreferrer"
          >
            {additionalButtons.learn_more_button_label || 'Learn More'}
          </a>
          : ''}
      </div>
      <div className={classNames(style.plan_space, props.spacerClassName)}/>
    </div>
  );
};

export default Plan;
